import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { OAuthClientAuth, OAuthCounters } from '@recruitee/http/types';
import {
  OAuthClientAuthActions,
  OAuthClientAuthSelectors,
  OAuthCountersActions,
  OAuthCountersSelectors,
} from '@recruitee/oauth/data';

import { ZapierIntegrationComponent } from '../zapier-integration/zapier-integration.component';

@Component({
  selector: 'rt-zapier-container',
  templateUrl: './zapier-container.component.html',
  standalone: true,
  imports: [ZapierIntegrationComponent],
})
export class ZapierContainerComponent implements OnInit, OnDestroy {
  @ViewChild(ZapierIntegrationComponent, { read: ZapierIntegrationComponent, static: true })
  public zapierIntegration: ZapierIntegrationComponent;

  public authorizedClientsLoading = false;
  public authorizedClientsLoaded = false;
  public authorizedClientsCounters: OAuthCounters | null = null;
  public authorizedClients: Record<string, OAuthClientAuth> | null = null;

  public removeActionConfig = {
    handler: () => {
      this.zapierIntegration.deauthorize();
    },
  };

  private ngOnDestroy$ = new Subject<void>();

  constructor(private store: Store) {}

  public ngOnInit(): void {
    combineLatest({
      isLoaded: this.store.select(OAuthClientAuthSelectors.isLoaded),
      isPending: this.store.select(OAuthClientAuthSelectors.isPending),
      data: this.store.select(OAuthClientAuthSelectors.data).pipe(
        map(clientAuths =>
          (clientAuths || []).reduce((acc, clientAuth) => {
            acc[clientAuth.client.name] = clientAuth;
            return acc;
          }, <Record<string, OAuthClientAuth>>{}),
        ),
        filterBoolean,
      ),
    })
      .pipe(debounceTime(0), takeUntil(this.ngOnDestroy$))
      .subscribe(({ isLoaded, isPending, data }) => {
        this.authorizedClientsLoaded = isLoaded;
        this.authorizedClientsLoading = isPending;
        this.authorizedClients = data;
      });

    this.store
      .select(OAuthCountersSelectors.data)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(data => {
        this.authorizedClientsCounters = data;
      });

    this.reloadAuthorizedClients();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public integrate(): void {
    this.zapierIntegration.authorize();
  }

  public reloadAuthorizedClients(): void {
    this.store.dispatch(OAuthClientAuthActions.fetch());
    this.store.dispatch(OAuthCountersActions.fetch());
  }
}
